import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    AUTH_LOG_PREFIX,
    AuthenticationConstants,
    clearAuthSession,
    consumePostLoginPath,
    getCloudEnv,
    getOAuthClientId
} from '../../utilities/authConstants';

/*
 * SDK is loaded via the CDN <script> tag in public/index.html. Importing the
 * npm package directly does NOT work under CRA / webpack 5 (see the long
 * comment block in PureCloudLogin.js).
 */
const getPlatformClient = () => {
    const w = window;
    return w.require && w.require('platformClient');
};

const OAuthCallback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const exchangeStartedRef = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // React 18 StrictMode invokes effects twice in dev. Auth codes are
        // single-use; the second exchange would always 400. Refuse re-entry.
        if (exchangeStartedRef.current) {
            return;
        }
        exchangeStartedRef.current = true;

        const run = async () => {
            console.debug(AUTH_LOG_PREFIX, 'OAuthCallback: starting token exchange');
            const searchParams = new URLSearchParams(location.search);
            const authCode = searchParams.get('code');
            const oauthError = searchParams.get('error');

            // Genesys may redirect back with ?error=... when the user denies
            // consent or the request is malformed.
            if (oauthError) {
                console.error(AUTH_LOG_PREFIX, 'OAuth provider returned error', oauthError);
                clearAuthSession();
                navigate('/purecloudlogin');
                return;
            }

            // Guard: missing code -> user landed here directly.
            if (!authCode) {
                console.warn(AUTH_LOG_PREFIX, 'OAuthCallback: no ?code in URL');
                navigate('/purecloudlogin');
                return;
            }

            // Guard: missing verifier -> SDK state lost (storage cleared,
            // different tab, etc.). Restart auth cleanly.
            let codeVerifier = null;
            try {
                codeVerifier = sessionStorage.getItem(AuthenticationConstants.CodeVerifier);
            } catch (storageErr) {
                console.error(AUTH_LOG_PREFIX, 'sessionStorage unavailable', storageErr);
            }
            if (!codeVerifier) {
                console.warn(AUTH_LOG_PREFIX, 'OAuthCallback: codeVerifier missing');
                clearAuthSession();
                navigate('/purecloudlogin');
                return;
            }

            // Guard: missing env -> cannot pick a region.
            const env = getCloudEnv();
            if (!env) {
                console.warn(AUTH_LOG_PREFIX, 'OAuthCallback: env missing');
                navigate('/purecloudlogin');
                return;
            }

            const clientId = getOAuthClientId();
            if (!clientId) {
                setError({ kind: 'fatal', message: 'OAuth client id is not configured.' });
                return;
            }

            const redirectUri = `${window.location.protocol}//${window.location.host}/oauth/callback`;

            const platformClient = getPlatformClient();
            if (!platformClient || !platformClient.ApiClient) {
                console.error(AUTH_LOG_PREFIX, 'Genesys SDK not loaded');
                setError({ kind: 'fatal', message: 'OAuth library failed to load.' });
                return;
            }
            const client = platformClient.ApiClient.instance;

            try {
                client.setEnvironment(env);
            } catch (envErr) {
                console.error(AUTH_LOG_PREFIX, 'Invalid Genesys environment', envErr);
                navigate('/unauthorized', { state: { error: `Invalid environment "${env}"` } });
                return;
            }

            try {
                const tokenResponse = await client.authorizePKCEGrant(
                    clientId,
                    codeVerifier,
                    authCode,
                    redirectUri
                );

                const accessToken = tokenResponse && tokenResponse.accessToken;
                const expiryRaw = tokenResponse && tokenResponse.tokenExpiryTime;

                // Guard: success-with-no-token is a failure.
                if (!accessToken) {
                    console.error(AUTH_LOG_PREFIX, 'authorizePKCEGrant returned no accessToken');
                    clearAuthSession();
                    navigate('/purecloudlogin');
                    return;
                }

                try {
                    sessionStorage.setItem(AuthenticationConstants.CloudCSPToken, accessToken);
                    if (expiryRaw !== undefined && expiryRaw !== null) {
                        const expireOn = typeof expiryRaw === 'number'
                            ? expiryRaw
                            : Date.parse(String(expiryRaw));
                        if (Number.isFinite(expireOn)) {
                            sessionStorage.setItem(AuthenticationConstants.ExpireOn, String(expireOn));
                        }
                    }
                    // Verifier is single-use. Drop it now so a refresh of
                    // /oauth/callback can never replay (the code itself is
                    // also server-side single-use; defence in depth).
                    sessionStorage.removeItem(AuthenticationConstants.CodeVerifier);
                } catch (storageErr) {
                    console.error(AUTH_LOG_PREFIX, 'Failed to persist token', storageErr);
                    setError({ kind: 'fatal', message: 'Browser storage is unavailable. Please disable private mode and retry.' });
                    return;
                }

                console.debug(AUTH_LOG_PREFIX, 'OAuthCallback: token persisted');
                const target = consumePostLoginPath() || '/';
                navigate(target);
            } catch (err) {
                console.error(AUTH_LOG_PREFIX, 'authorizePKCEGrant failed', err);
                const status = err && (err.status || (err.response && err.response.status));
                const code = err && (err.code || (err.body && err.body.code));

                // Branch on failure mode.
                if (status === 400 || code === 'invalid_grant') {
                    clearAuthSession();
                    navigate('/purecloudlogin');
                    return;
                }
                if (status === 401 || code === 'bad.credentials') {
                    clearAuthSession();
                    navigate('/purecloudlogin');
                    return;
                }
                if (status === 403) {
                    navigate('/unauthorized');
                    return;
                }
                // 5xx / network: offer a retry instead of looping.
                setError({
                    kind: 'retry',
                    message: 'We could not complete sign-in due to a network or server error.'
                });
            }
        };

        run().catch((err) => {
            // Any unexpected throw lands here. Without this catch, an
            // unhandled rejection would silently hang the spinner.
            console.error(AUTH_LOG_PREFIX, 'OAuthCallback fatal', err);
            setError({ kind: 'fatal', message: 'An unexpected error occurred during sign-in.' });
        });
    }, [location.search, navigate]);

    if (error) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <p>{error.message}</p>
                {error.kind === 'retry' && (
                    <button
                        type="button"
                        onClick={() => {
                            clearAuthSession();
                            navigate('/purecloudlogin');
                        }}
                    >
                        Try again
                    </button>
                )}
            </div>
        );
    }

    return (
        <Fragment>
            <div style={{ padding: 24, textAlign: 'center' }}>
                Completing sign-in...
            </div>
        </Fragment>
    );
};

export default OAuthCallback;
